// ステータス計算系のロジックをlibに集約
import { PokemonDefenceInterface } from '../pokemon/pokemon';
import type { PersonalityRate } from '../types/damage';
import {
  calcRealValueHPStat,
  calcRealValueAttackDefencePokemon,
  calcRealValueSpecialAttackDefencePokemon,
  calcRealValueDeffenceDefencePokemon,
  calcRealValueSpecialDeffenceDefencePokemon,
  calcRealValueSpeedDefencePokemon,
  calcRealValueSpeedOffencePokemon,
} from './stat/real-value';
import type { StatInputs } from '../types/stat';
import { MAX_STAT_EFFORT } from '../constants/effort';

export interface CalculatedStats {
  hp: number;
  attack: number;
  defense: number;
  specialAttack: number;
  specialDefense: number;
  speed: number;
}

export interface AttackStatInputs {
  attack_effort: number;
  special_attack_effort: number;
  defense_effort: number;
  special_defense_effort: number;
  hp_effort: number;
  speed_effort: number;
  attack_personality: PersonalityRate;
  special_attack_personality: PersonalityRate;
  defense_personality: PersonalityRate;
  special_defense_personality: PersonalityRate;
  speed_personality: PersonalityRate;
  attack_iv?: number;
  special_attack_iv?: number;
  defense_iv?: number;
  special_defense_iv?: number;
  speed_iv?: number;
}

const clampEffortPoint = (effort: number): number => {
  if (!Number.isFinite(effort)) return 0;
  return Math.max(0, Math.min(MAX_STAT_EFFORT, Math.floor(effort)));
};

// 防御側の実数値を算出
export const calculateActualStats = (
  pokemon: PokemonDefenceInterface,
  statInputs: StatInputs,
): CalculatedStats => {
  const hp = calcRealValueHPStat({
    ...pokemon,
    effective_slider_step: {
      ...pokemon.effective_slider_step,
      hp: clampEffortPoint(statInputs.hp_effort),
    },
  });

  const attack = calcRealValueAttackDefencePokemon({
    ...pokemon,
    effective_slider_step: {
      ...pokemon.effective_slider_step,
      attack: clampEffortPoint(statInputs.attack_effort),
    },
    personality: {
      ...pokemon.personality,
      attack: statInputs.attack_personality,
    },
  });

  const defense = calcRealValueDeffenceDefencePokemon({
    ...pokemon,
    effective_slider_step: {
      ...pokemon.effective_slider_step,
      defense: clampEffortPoint(statInputs.defense_effort),
    },
    personality: {
      ...pokemon.personality,
      defense: statInputs.defense_personality,
    },
  });

  const specialAttack = calcRealValueSpecialAttackDefencePokemon({
    ...pokemon,
    effective_slider_step: {
      ...pokemon.effective_slider_step,
      special_attack: clampEffortPoint(statInputs.special_attack_effort),
    },
    personality: {
      ...pokemon.personality,
      special_attack: statInputs.special_attack_personality,
    },
  });

  const specialDefense = calcRealValueSpecialDeffenceDefencePokemon({
    ...pokemon,
    effective_slider_step: {
      ...pokemon.effective_slider_step,
      special_defense: clampEffortPoint(statInputs.special_defense_effort),
    },
    personality: {
      ...pokemon.personality,
      special_defense: statInputs.special_defense_personality,
    },
  });

  const speed = calcRealValueSpeedDefencePokemon({
    ...pokemon,
    effective_slider_step: {
      ...pokemon.effective_slider_step,
      speed: clampEffortPoint(statInputs.speed_effort),
    },
    personality: {
      ...pokemon.personality,
      speed: statInputs.speed_personality,
    },
  });

  return { hp, attack, defense, specialAttack, specialDefense, speed };
};

// 攻撃側の必要な実数値を算出
export const calculateAttackPokemonStats = (
  pokemon: PokemonDefenceInterface,
  statInputs: AttackStatInputs,
) => {
  const attack = calcRealValueAttackDefencePokemon({
    ...pokemon,
    effective_slider_step: {
      ...pokemon.effective_slider_step,
      attack: clampEffortPoint(statInputs.attack_effort),
    },
    personality: {
      ...pokemon.personality,
      attack: statInputs.attack_personality,
    },
  });

  const specialAttack = calcRealValueSpecialAttackDefencePokemon({
    ...pokemon,
    effective_slider_step: {
      ...pokemon.effective_slider_step,
      special_attack: clampEffortPoint(statInputs.special_attack_effort),
    },
    personality: {
      ...pokemon.personality,
      special_attack: statInputs.special_attack_personality,
    },
  });

  const defense = calcRealValueDeffenceDefencePokemon({
    ...pokemon,
    effective_slider_step: {
      ...pokemon.effective_slider_step,
      defense: clampEffortPoint(statInputs.defense_effort),
    },
    personality: {
      ...pokemon.personality,
      defense: statInputs.defense_personality,
    },
  });

  return { attack, specialAttack, defense };
};

// 攻撃側ポケモンの素早さ実数値を算出
export const calculateOffenceSpeed = (pokemon: PokemonDefenceInterface): number => {
  return calcRealValueSpeedOffencePokemon(pokemon as any);
};
